import React, { useEffect, useState } from 'react';
import { GitBranch } from 'lucide-react';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useGitSyncStore } from '../../store/gitSyncStore';
import { useUiModalStore, setActiveTab } from '../../store/uiModalStore';
import { describeSync, isGitTracked, isLocalOnly, refreshGitStatus } from '../../services/git/gitSyncStatus';
import { openVaultGate, useVaultStore } from '../../store/vaultStore';

const RECHECK_MS = 5 * 60 * 1000;

/**
 * Status bar: the current workspace's git sync state on every page
 * ("Synced · 2 min ago", "Changes waiting", "Sync failed"); click → Git page.
 * Shown when the current workspace syncs to a remote or is tracked on this
 * device ("Saved on this device · 2 min ago"). Like VS Code's
 * sync indicator at the bottom left.
 */
export default function GitStatusBarItem() {
  // From the list, not `currentWorkspace` alone: git links restored at start
  // (useGitLinksHydration) land on the list, and the current object can lag.
  const workspace = useWorkspaceStore((s) => {
    const cur = s.currentWorkspace;
    if (!cur) return null;
    return (s.workspaces || []).find((w) => String(w.id) === String(cur.id)) || cur;
  });
  const linked = isGitTracked(workspace);
  const id = linked ? String(workspace.id) : null;
  const entry = useGitSyncStore((s) => (id ? s.byWorkspace[id] : null));
  const appPanel = useUiModalStore((s) => s.activeTab);
  const vaultUnlocked = useVaultStore((s) => !!s.status?.unlocked);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!linked) return undefined;
    refreshGitStatus(workspace);
    const recheck = setInterval(() => refreshGitStatus(workspace), RECHECK_MS);
    const clock = setInterval(() => tick((n) => n + 1), 60 * 1000); // "2 min ago" moves on
    return () => {
      clearInterval(recheck);
      clearInterval(clock);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!linked) return null;
  const local = isLocalOnly(workspace);
  const sync = describeSync(entry, { local });
  return (
    <button
      type="button"
      className={`devtools-footer__tab devtools-footer__tab--git ${appPanel === 'git' ? 'active' : ''}`}
      data-tone={sync.tone}
      data-testid="git-statusbar"
      // "Unlock the Vault to sync": the click unlocks instead of navigating.
      onClick={() => (sync.vaultLocked && !vaultUnlocked ? openVaultGate('git-sync') : setActiveTab('git'))}
      title={
        sync.vaultLocked
          ? sync.label
          : entry?.lastError ? `${local ? 'Save failed' : 'Sync failed'}: ${entry.lastError}` : `${workspace.name} — open the Git page`
      }
    >
      <GitBranch size={13} />
      <span>{sync.label}</span>
    </button>
  );
}
